import { useId, useState, type KeyboardEvent } from "react";
import type { Store } from "@tebiki/shared";
import { Icon } from "./Icon.js";

export interface LabelValue {
  storeId: string | null;
  category: string;
  tags: string[];
}

interface LabelingBlockProps {
  value: LabelValue;
  onChange: (next: LabelValue) => void;
  stores: Store[];
  /** existing categories across the org → combobox suggestions (prevents 接客 vs 接客対応 drift) */
  categorySuggestions: string[];
  /** compact = inline on the Preview cover; default = full block on the Picker */
  compact?: boolean;
}

const MUST_TAG = "必読";

/**
 * Creation-time labeling: 店舗 select + カテゴリ combobox (datalist of existing categories) +
 * タグ chips + a 必読 toggle, with a LIVE mini book-spine preview so the creator literally sees
 * which (store × category) book this manual will join. Token-only → themes to field(dark) on the
 * Picker and console(light) on Preview. Controlled; the parent persists via manuals.update.
 */
export function LabelingBlock({ value, onChange, stores, categorySuggestions, compact }: LabelingBlockProps) {
  const listId = useId();
  const [tagDraft, setTagDraft] = useState("");
  const must = value.tags.includes(MUST_TAG);

  function set(patch: Partial<LabelValue>) {
    onChange({ ...value, ...patch });
  }
  function addTag(raw: string) {
    const t = raw.trim();
    if (!t || value.tags.includes(t)) return;
    set({ tags: [...value.tags, t] });
    setTagDraft("");
  }
  function removeTag(t: string) {
    set({ tags: value.tags.filter((x) => x !== t) });
  }
  function onTagKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag(tagDraft);
    } else if (e.key === "Backspace" && !tagDraft && value.tags.length) {
      removeTag(value.tags[value.tags.length - 1]);
    }
  }
  function toggleMust() {
    set({ tags: must ? value.tags.filter((t) => t !== MUST_TAG) : [...value.tags, MUST_TAG] });
  }

  return (
    <div
      className="labeling"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: compact ? 14 : 18,
        alignItems: "start",
        padding: compact ? 0 : "4px 0",
      }}
    >
      <div style={{ display: "grid", gap: 14, minWidth: 0 }}>
        <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 200px", minWidth: 0 }}>
            <label className="field-label" htmlFor={`${listId}-store`}>店舗</label>
            <select
              id={`${listId}-store`}
              className="select"
              value={value.storeId ?? ""}
              onChange={(e) => set({ storeId: e.target.value || null })}
            >
              <option value="">店舗を選択…</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 200px", minWidth: 0 }}>
            <label className="field-label" htmlFor={`${listId}-cat`}>カテゴリ</label>
            <input
              id={`${listId}-cat`}
              className="input jp"
              list={`${listId}-catlist`}
              value={value.category}
              placeholder="例: 接客 / 清掃 / レジ"
              onChange={(e) => set({ category: e.target.value })}
              autoComplete="off"
            />
            <datalist id={`${listId}-catlist`}>
              {categorySuggestions.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
        </div>

        <div>
          <label className="field-label">タグ</label>
          <div
            className="row"
            style={{ gap: 6, flexWrap: "wrap", alignItems: "center", border: "1px solid var(--line)", borderRadius: 12, padding: "8px 10px", background: "var(--surface)" }}
          >
            {value.tags.map((t) => (
              <span key={t} className="tag" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                {t === MUST_TAG ? <Icon name="star" size={12} /> : null}
                {t}
                {/* padding + negative margin: keeps the chip's look, grows the hit area to ~36px */}
                <button type="button" aria-label={`「${t}」を削除`} onClick={() => removeTag(t)} style={{ display: "inline-flex", color: "var(--muted)", padding: 12, margin: "-12px -10px -12px -7px" }}>
                  <Icon name="close" size={12} />
                </button>
              </span>
            ))}
            <input
              className="jp"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={onTagKey}
              onBlur={() => addTag(tagDraft)}
              placeholder={value.tags.length ? "追加…" : "タグを入力してEnter"}
              aria-label="タグを追加"
              style={{ flex: "1 1 120px", minWidth: 80, border: 0, background: "none", color: "var(--ink)", outline: "none", fontSize: 14, padding: "4px 2px" }}
            />
          </div>
          <button
            type="button"
            aria-pressed={must}
            onClick={toggleMust}
            className="tag"
            style={{
              marginTop: 8,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
              borderColor: must ? "var(--hi-deep)" : "var(--line-2)",
              color: must ? "var(--ink)" : "var(--muted)",
              background: must ? "color-mix(in srgb,var(--hi) 16%,transparent)" : "transparent",
            }}
          >
            <Icon name={must ? "check" : "star"} size={14} /> 必読タグを付ける（表紙・印刷に表示。スタッフへの割当は本の「必読」設定で）
          </button>
        </div>
      </div>

      {/* ミニ背表紙プレビューは撤去: 本は実体エンティティになり、店舗×カテゴリはもう本棚の
          配置を決めない（存在しない本を約束するプレビューは提出時の本選択と混同を招く）。 */}
    </div>
  );
}
